import { useSnackbar } from 'notistack';
import { useContext, useEffect, useState } from 'react';
import { GroupMap, PasswordCredential, RESTScimAppConfig, RoleObject } from '../interfaces/ApplicationInterfaces';
import { AdminContext } from '../contexts/AdminContext';
import { COSMOS_API, GET_ENDPOINT, POST_ENDPOINT, IEF_API, IEF_API_POST_ENDPOINT } from '../api/API';
import { Organization } from '../interfaces/APIInterface';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { AppEnvSetting, isNullUndefEmpty } from '../ts/common';
import { AppSecrets } from './AppSecrets';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog/Dialog';
import DialogTitle from '@mui/material/DialogTitle/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText/DialogContentText';
import DialogContent from '@mui/material/DialogContent/DialogContent';
import DialogActions from '@mui/material/DialogActions/DialogActions';
import TableContainer from '@mui/material/TableContainer/TableContainer';
import TableBody from '@mui/material/TableBody/TableBody';
import Table from '@mui/material/Table/Table';
import TableRow from '@mui/material/TableRow/TableRow';
import TableCell from '@mui/material/TableCell/TableCell';
import TableHead from '@mui/material/TableHead/TableHead';
import IconButton from '@mui/material/IconButton/IconButton';
import CloseIcon from '@mui/icons-material/Close';

interface GroupMapWrapperProps {
  allGroupsData: any;
  actionClick: any;
}
const GroupMapWrapper = ({ allGroupsData, actionClick }: GroupMapWrapperProps) => {
  const [selectedGroupId, setSelectedGroupId] = useState<string>("");
  const [selectedGroupName, setSelectedGroupName] = useState<string>("");
  const [groupToMap, setGroupToMap] = useState<string>("");
  return <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
    <Autocomplete
      className="roleSelect"
      disableClearable
      fullWidth
      options={
        Object.keys(allGroupsData).map((groupName: string) => {
          return { id: allGroupsData[groupName]['id'], name: allGroupsData[groupName]['displayName'] };
        })
      }
      getOptionLabel={(option: { id: string; name: string }) => option.name}
      value={Object.keys(allGroupsData).map((groupName: string) => {
        return { id: allGroupsData[groupName]['id'], name: allGroupsData[groupName]['displayName'] };
      }).find((val: any) => val.id === selectedGroupId)}
      renderInput={(params) => (
        <TextField {...params} name="group-select" label="Select a Group" />
      )}
      onChange={
        (_: any, value: { id: string; name: string } | null) => {
          if (value === null) return;
          setSelectedGroupId(value.id);
          setSelectedGroupName(value.name);
        }
      }
    />
    <TextField
      name="groupToMap"
      sx={{ display: 'flex', flexDirection: 'row' }}
      value={groupToMap || null}
      onChange={(e) => setGroupToMap(e.target.value)}
      label="External Group Id to Map"
      fullWidth
    />
    <Button fullWidth
      sx={{ minWidth: '50px' }}
      disabled={selectedGroupId === '' || groupToMap === ""}
      variant='contained'
      onClick={() => actionClick(selectedGroupId, selectedGroupName, groupToMap)}
    >
      Map Group
    </Button>
  </Box>
}


const SCIMApp = () => {
  const [tabValue, setTabValue] = useState(0);
  const { adminCredentials, getToken } = useContext(AdminContext);
  const [organizations, setOrganizations] = useState<
    Array<{ id: string; name: string; protocol: string }>
  >([]);
  const [scimAppData, setscimAppData] = useState<any>(null);
  const [hasScimAccess, setHasScimAccess] = useState<boolean>(false);
  const [selectedOrg, setSelectedOrg] = useState<string>(
    adminCredentials.organizationList?.length > 1 ? "" : adminCredentials.organizationId
  );
  const [sendEmailNotification, setSendEmailNotification] = useState<boolean>(false);
  const [groupMapping, setGroupMapping] = useState<Record<string, GroupMap>>({});
  const [allGroupsData, setAllGroupsData] = useState<any>(null);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  interface CsvData {
    groupName: string; // Assuming string values for simplicity
    groupId: string;
  }
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<CsvData[]>([]);
  const [openModal, setOpenModal] = useState<string>("");
  const [missingGroups, setMissingGroups] = useState<Record<string, string>>({});

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      console.log("inside File change");
      setCsvFile(e.target.files[0]);
      handleSubmit(e.target.files[0]);
    }
  };

  const handleSubmit = (csvFile: File) => {
    if (csvFile) {
      console.log("csvFile :", csvFile, csvFile.name);
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim() !== ''); // Filter out empty lines
        if (lines.length > 0) {
          const headers = lines[0].trim().split(',');
          console.log("headers :", headers);
          if (headers[0] != "groupName" || headers[1] != "groupId") {
            enqueueSnackbar('The CSV file should have the first column as groupName and the second column as groupId', {
              variant: 'error',
            });
            setOpenModal("");
            return;
          }
          const data: CsvData[] = lines.slice(1).map(line => {
            const values = line.trim().split(',');
            let row: CsvData = {
              "groupName": values[0]?.trim() || "",
              "groupId": values[1]?.trim() || ""
            };
            return row;
          });
          setParsedData(data);
          addGroupMapping(data);
        }
      };
      reader.readAsText(csvFile);
      
      
    }
  };

  interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
  }

  function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
      <div
        key={index}
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  }

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  useEffect(() => {
    /* https://stackoverflow.com/a/60907638 */
    let isMounted = true;

    // Populate Org List
    if (organizations === undefined || organizations.length === 0) {
      if (
        adminCredentials.organizationList === undefined ||
        adminCredentials.organizationList === null ||
        adminCredentials.organizationList.length === 0
      ) {
        COSMOS_API.GET(GET_ENDPOINT.GetOrganizationsList, getToken()).then(
          (res) => {
            if (isMounted) {
              if (res.ok) {
                setOrganizations(
                  res.respBody.organizations.sort(
                    (a: Organization, b: Organization) =>
                      a.name <= b.name ? -1 : 1
                  )
                );
                if ((!adminCredentials.multiOrg) || res.respBody.organizations.length == 1) {
                  if (res.respBody.organizations.length > 0)
                    setSelectedOrg(res.respBody.organizations[0].id);
                }
              } else {
                enqueueSnackbar('Error fetching Organization List', {
                  variant: 'error',
                });
              }
            }
          }
        );
      } else {
        if (isMounted) {
          setOrganizations([...adminCredentials.organizationList]);
          if ((!adminCredentials.multiOrg) || adminCredentials.organizationList.length == 1) {
            setSelectedOrg(adminCredentials.organizationList[0].id);
          }
        }
      }
    }
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isNullUndefEmpty(selectedOrg)) {
      console.log("org selected :", selectedOrg);
      setscimAppData(null);
      getSCIMAppData();
    }
  }, [selectedOrg]);

  async function getAllGroupsData() {
    console.log("callomg allGroupsData");
    let allGroupsDataResp = await COSMOS_API.POST(
      {},
      POST_ENDPOINT.GetAllGroupsList,
      getToken()
    );
    if (
      allGroupsDataResp.ok &&
      allGroupsDataResp.respBody !== undefined &&
      allGroupsDataResp.respBody !== null
    ) {
      console.log(allGroupsDataResp);
      let temp = allGroupsDataResp.respBody.groups.reduce((acc: any, group: any) => {
        acc = { ...acc, [group.displayName.toLowerCase()]: group }
        return acc;
      }, {});
      console.log("temp :", temp);
      setAllGroupsData(temp);
    }
  }
  async function getSCIMAppData() {
    let scimAppDataResp = await COSMOS_API.POST(
      { id: selectedOrg },
      POST_ENDPOINT.GetSCIMApplicationData,
      getToken()
    );

    if (
      scimAppDataResp.ok &&
      scimAppDataResp.respBody !== undefined &&
      scimAppDataResp.respBody !== null
    ) {
      console.log("resp ", scimAppDataResp);
      setscimAppData(scimAppDataResp.respBody);
      console.log("scimData :", scimAppDataResp.respBody);
      setGroupMapping(scimAppDataResp.respBody.groupMapping);
      setSendEmailNotification(scimAppDataResp.respBody.emailNotification || false);
      getAllGroupsData();
    }
    else {
      if (!scimAppDataResp.ok && scimAppDataResp.status == 403) {
        setscimAppData(null);
        setHasScimAccess(false);
      }
      else {
        setscimAppData({});
        setHasScimAccess(true);
      }
    }
  }

  const refreshAppList = async (appId: string) => {
    getSCIMAppData();
  };

  function deleteGroupMapping(id: string) {
    let _temp = Object.assign({}, groupMapping);
    delete _temp[id];
    setGroupMapping(_temp);
  }

  function createGroupMapping(selectedGroupId: string, selectedGroupName: string, groupToMap: string) {
    setGroupMapping({ ...groupMapping, [selectedGroupId]: { groupName: selectedGroupName, clientGroupId: groupToMap.trim() } });

  }

  async function createSCIM() {
    enqueueSnackbar('Creating SCIM Application please wait.', { variant: 'info' });
    // setUsers([]);
    const appData: RESTScimAppConfig = {
      orgId: selectedOrg,
      tenantId: AppEnvSetting.tenantId,
      id: scimAppData.id,
      appId: scimAppData.appId,
      // signInTrigger: signInTrigger,
      isActive: true,
      emailNotification: false,
      groupMapping: {},
      createUserTrigger: {
        endpoint: null,
        passUserObject: false,
        body: null,
      },
      updateUserTrigger: {
        endpoint: null,
        passUserObject: false,
        body: null,
      },
      deactivateUserTrigger: {
        endpoint: null,
        passUserObject: false,
        body: null,
      },
      reactivateUserTrigger: {
        endpoint: null,
        passUserObject: false,
        body: null,
      },
      deleteUserTrigger: {},
      secrets: [],
    };

    let resp = await IEF_API.POST(
      appData,
      IEF_API_POST_ENDPOINT.CreateSCIMApplication,
      getToken()
    );

    console.log(resp);
    if (resp.ok) {
      enqueueSnackbar('SCIM Application created successfully.', { variant: 'success' });
      getSCIMAppData();
    } else {
      enqueueSnackbar('Error creating SCIM Application : ' + resp?.respBody?.message, { variant: 'error' });
    }
  }

  const saveSCIMConfiguration = async () => {
    enqueueSnackbar('Saving scim group mapping.', { variant: 'info' });

    const appData: RESTScimAppConfig = {
      ...scimAppData,
      groupMapping: groupMapping,
      emailNotification: sendEmailNotification
    };
    console.log(
      JSON.stringify(
        appData
      )
    );
    let resp;
    try {
      resp = await COSMOS_API.POST(
        appData,
        POST_ENDPOINT.SaveSCIMApplicationDataAdmin,
        getToken()
      );

      if (!resp.ok) {
        enqueueSnackbar('Network error', { variant: 'error' });
        console.error(resp);
        return;
      }
    } catch (err) {
      console.log(err);
      enqueueSnackbar('Error updating SCIM Group mapping', { variant: 'error' });
      return;
    }
    enqueueSnackbar('Successfully saved SCIM Group mapping', { variant: 'success' });

  };

  const autoCompleteSingleSelect = (
    id: string,
    label: string,
    selectedVal: string,
    setSelectedVal:
      | React.Dispatch<React.SetStateAction<string>>
      | ((arg0: string) => void),
    possibleVals: Array<{ id: string; name: string; protocol: string }>
  ) => {
    console.log({ id, label, selectedVal, possibleVals });
    return (
      <Autocomplete
        id={id}
        fullWidth
        disableClearable
        value={possibleVals.find((val) => val.id === selectedVal)}
        isOptionEqualToValue={(
          option: { id: string; name: string },
          value: { id: string; name: string }
        ) => option.id === value.id}
        options={possibleVals.filter(_org => _org.protocol != "local")}
        getOptionLabel={(option: { id: string; name: string }) => option.name}
        renderInput={(params) => <TextField {...params} label={label} />}
        onChange={(_: any, value: { id: string; name: string } | null) => {
          if (value === null) return;
          setSelectedVal(value.id);
        }}
      />
    );
  };

  const addGroupMapping = (parsedData:any) => {
    let tempMapping = groupMapping;
    let _missingGroups: Record<string, string> = {};
    parsedData.map((item: any, index: number) => {
      if (allGroupsData.hasOwnProperty(item["groupName"].toLowerCase())) {
        let id = allGroupsData[item["groupName"].toLowerCase()]['id'];
        tempMapping = { ...tempMapping, [id]: { "groupName": allGroupsData[item["groupName"].toLowerCase()]['displayName'], "clientGroupId": item["groupId"] } }
      } else {
        _missingGroups[index + 2] = item["groupName"];
      }
    })
    setGroupMapping(tempMapping);

    if (Object.keys(_missingGroups).length > 0) {
      setMissingGroups(_missingGroups);
      setOpenModal("result");
    } else {
      setOpenModal("");
    }
  }

  return (
    <>
      <div className="full-section-container">
        <Container>
          <Stack spacing={4}>
            <Stack spacing={2}>
              {adminCredentials.multiOrg &&
                organizations.length > 1 &&
                autoCompleteSingleSelect(
                  'org',
                  'Federated Organization',
                  selectedOrg,
                  setSelectedOrg,
                  organizations
                )}
              {(selectedOrg && selectedOrg !== '') && (!scimAppData || Object.keys(scimAppData).length == 0) &&
                <Button
                  disabled={!hasScimAccess || (!selectedOrg) || (!scimAppData) || selectedOrg === '' || Object.keys(scimAppData).length > 0}
                  variant='contained'
                  onClick={createSCIM}
                >
                  Create SCIM Application
                </Button>
              }


              {scimAppData && Object.keys(scimAppData).length > 0 &&
                <Box sx={{ width: '100%' }}>
                  <Paper sx={{ width: '100%', mb: 2 }}>
                    <Typography sx={{ pt: 1, pb: 2, ml: 2 }}>Application (Client) Id : <b>{scimAppData.appId}</b></Typography>
                    <Tabs value={tabValue} onChange={(event: React.SyntheticEvent, newValue: number) => setTabValue(newValue)}>
                      <Tab label="Generate Secret" {...a11yProps(0)} />
                      <Tab label="Group Mapping and other settings" {...a11yProps(1)} />
                    </Tabs>
                    <CustomTabPanel value={tabValue} index={0}>
                      <AppSecrets secrets={scimAppData.secrets} orgId={selectedOrg} appId={scimAppData.appId || ""} reloadAppData={refreshAppList} />
                    </CustomTabPanel>
                    <CustomTabPanel value={tabValue} index={1}>
                      {!allGroupsData || Object.keys(allGroupsData).length === 0 && <Typography>There are no groups available for mapping.</Typography>}
                      {allGroupsData && Object.keys(allGroupsData).length > 0 &&
                        <>
                          <Box>
                            Group Mapping count: {Object.keys(groupMapping).length}
                          </Box>
                          <Box>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={sendEmailNotification}
                                  onChange={(e: any) => {
                                    if (e) setSendEmailNotification(e.target.checked);
                                  }}
                                />
                              }
                              label="Email setup instructions to user?"
                            />
                          </Box>
                          <Box sx={{ display: "flex", gap: 2 }}>
                            <Button
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={() => {
                                setGroupMapping({});
                                setMissingGroups({})
                              }}
                            >
                              Remove Existing Group Mapping
                            </Button>
                            <TextField
                              id="file-name"
                              size='small'
                              className={csvFile === null ? '' : 'file-name-present'}
                              value={csvFile ? csvFile.name : ''}
                              disabled
                              InputProps={{
                                endAdornment: (
                                  <Button component="label" className="upload-file-input">
                                    <input
                                      hidden
                                      className="hide"
                                      type="file"
                                      name="group-mapping"
                                      id="group-mapping"
                                      accept=".csv"
                                      onChange={handleFileChange}
                                    />
                                    Select CSV
                                  </Button>
                                ),
                              }}
                            />
                            {Object.keys(missingGroups).length > 0 &&
                            <Button color="error" variant='outlined' onClick={()=>setOpenModal("result")}>See Error Result</Button> }
                          </Box>

                          <Dialog open={openModal == "result"} onClose={() => setOpenModal("")}>
                            <DialogTitle><Typography >Result</Typography></DialogTitle>
                            <IconButton
                              aria-label="close"
                              onClick={()=> setOpenModal("")}
                              sx={(theme) => ({
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                color: theme.palette.grey[500],
                              })}
                            >
                              <CloseIcon />
                            </IconButton>
                            <DialogContent>
                              <b>Total Attempted : </b> {Object.keys(parsedData).length}<br/>
                              <b>Succeeded : </b> {Object.keys(parsedData).length - Object.keys(missingGroups).length}<br/><br/>
                              <b>Failed (Group does not exist internally)</b>
                              
                              <TableContainer component={Paper}>
                                <Table size='medium'>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Row</TableCell>
                                      <TableCell>Internal Group Name</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {Object.keys(missingGroups).map((row: string) => {
                                      return (
                                        <TableRow
                                          key={row}
                                          className="row-no"
                                        >
                                          <TableCell>{row}</TableCell>
                                          <TableCell>{missingGroups[row]}</TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </DialogContent>
                          </Dialog>
                          
                          <Box sx={{ display: 'flex', flexDirection: 'column', marginTop: '20px' }}>

                            {groupMapping && Object.keys(groupMapping).length > 0 && Object.keys(groupMapping).map((el, index) => {

                              return <Box key={index} sx={{ display: 'flex', flexDirection: 'row', gap: 2, mb: 2 }}>
                                <TextField
                                  disabled
                                  sx={{ display: 'flex', flexDirection: 'row' }}
                                  value={groupMapping[el]['groupName']}
                                  label="Group Name"
                                  fullWidth
                                />
                                <TextField
                                  disabled
                                  sx={{ display: 'flex', flexDirection: 'row' }}
                                  value={groupMapping[el].clientGroupId.trim()}
                                  label="Group Name Mapped"
                                  fullWidth
                                />
                                <Button fullWidth
                                  sx={{ minWidth: '50px' }}
                                  // disabled={() || (!scimAppData) || selectedOrg === '' || Object.keys(scimAppData).length > 0}
                                  variant='contained'
                                  onClick={() => deleteGroupMapping(el)}
                                >
                                  Delete
                                </Button>
                              </Box>

                            })}
                            <Typography variant='h6'>Add Group Mapping for SCIM</Typography>
                            {allGroupsData != null && Object.keys(allGroupsData).length > 0 &&
                              <>
                                <GroupMapWrapper allGroupsData={allGroupsData}
                                  actionClick={(selectedGroupId: string, selectedGroupName: string, groupToMap: string) => createGroupMapping(selectedGroupId, selectedGroupName, groupToMap)} />
                                <Button fullWidth
                                  sx={{ minWidth: '50px', mt: 2 }}
                                  disabled={!scimAppData}
                                  variant='contained'
                                  onClick={saveSCIMConfiguration}
                                >
                                  Save Configuration
                                </Button>
                              </>
                            }
                          </Box>
                        </>
                      }
                    </CustomTabPanel>


                  </Paper>
                </Box>

              }

            </Stack>
          </Stack>
        </Container>
      </div>
    </>
  );
};

export default SCIMApp;
